import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROOMS, ROOM_SLUGS } from '../config/rooms';

const TRACKS = [
  '/sounds/1.mp3',
  '/sounds/2.mp3',
  '/sounds/3.mp3',
  '/sounds/4.mp3',
  '/sounds/5.mp3',
  '/sounds/6.mp3',
  '/sounds/7.mp3',
];

function shuffledTracks() {
  const arr = [...TRACKS];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const ResolutionBadge = () => {
  const [res, setRes] = useState(`${window.innerWidth}x${window.innerHeight}`);
  useEffect(() => {
    const update = () => setRes(`${window.innerWidth}x${window.innerHeight}`);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return (
    <div style={{
      position: 'fixed', bottom: 4, left: 6, zIndex: 9999,
      fontSize: 10, color: 'rgba(0,0,0,0.35)', pointerEvents: 'none',
      fontFamily: 'monospace'
    }}>{res}</div>
  );
};

const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const servicesDropdownRef = useRef(null);

  // --- Ambient music ---
  const [isMuted, setIsMuted] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const audioRef = useRef(null);
  const playlistRef = useRef(shuffledTracks());
  const trackIndexRef = useRef(0);

  const playNextTrack = useCallback(() => {
    trackIndexRef.current = (trackIndexRef.current + 1) % playlistRef.current.length;
    if (trackIndexRef.current === 0) {
      playlistRef.current = shuffledTracks();
    }
    if (audioRef.current) {
      audioRef.current.src = playlistRef.current[trackIndexRef.current];
      audioRef.current.play().catch(() => {});
    }
  }, []);

  // Inicializar el elemento audio
  useEffect(() => {
    const audio = new Audio(playlistRef.current[0]);
    audio.volume = 0.35;
    audio.addEventListener('ended', playNextTrack);
    audioRef.current = audio;

    // Intentar arrancar automáticamente; si el navegador lo bloquea, esperar interacción
    const startOnInteraction = () => {
      audio.play().then(() => {
        setHasStarted(true);
      }).catch(() => {});
      document.removeEventListener('click', startOnInteraction);
      document.removeEventListener('keydown', startOnInteraction);
    };

    audio.play().then(() => {
      setHasStarted(true);
    }).catch(() => {
      // Autoplay bloqueado por el navegador: esperar primer gesto del usuario
      document.addEventListener('click', startOnInteraction);
      document.addEventListener('keydown', startOnInteraction);
    });

    return () => {
      audio.removeEventListener('ended', playNextTrack);
      audio.pause();
      document.removeEventListener('click', startOnInteraction);
      document.removeEventListener('keydown', startOnInteraction);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    if (!hasStarted) {
      audioRef.current.play().then(() => setHasStarted(true)).catch(() => {});
    }
    const next = !isMuted;
    audioRef.current.muted = next;
    setIsMuted(next);
  }, [isMuted, hasStarted]);

  // Las salas y sus páginas se definen en src/config/rooms.js, que es también
  // el mapa que usan las URLs directas /sala/:slug
  const services = ROOM_SLUGS.map((slug) => ({
    slug,
    label: ROOMS[slug].label,
    servicePage: ROOMS[slug].servicePage,
    priorPage: ROOMS[slug].entryPage,
  }));

  const menuItems = [
    { id: 0, label: "Inicio", path: "/", isHome: true },
    { id: 'Servicios', label: "Servicios", isServices: true },
    { id: 'TestTDAH', label: "Test TDAH", path: "/test-tdah" },
    { id: 'Immersive_Blog', label: "Blog", path: "https://centromilos.com/", isExternal: true },
    { id: 'Immersive_Nosotros', label: "Noticias", path: "https://www.clinicamilos.com/noticias-clinica-especialistas-en-tdah/", isExternal: true },
    { id: 'Immersive_Contacto', label: "Contacto", path: "https://www.clinicamilos.com/contacto-directo/", isExternal: true },
  ];

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleServiceClick = (service) => {
    // Se navega a la URL de la sala en vez de despachar 'voiceflow-navigate'.
    //
    // Aquel evento marca la navegación como originada por Voiceflow (vfNavRef),
    // con lo que HomePage NO enviaba el intent pageNavigationInfo: la sala se
    // veía en pantalla pero el agente seguía en el contexto de recepción.
    // Yendo por la ruta, la entrada es una navegación normal del usuario y la
    // sincronización con Voiceflow ocurre como en el resto del recorrido.
    navigate(`/sala/${service.slug}`);
    setIsServicesDropdownOpen(false);
    setIsMenuOpen(false);
  };

  const handleNavigation = (item) => {
    if (item.path) {
      navigate(item.path);
    }
    setIsMenuOpen(false);
  };

  return (
    <div>

      <nav
        className="fixed top-0 left-0 right-0 z-[200] 
                    backdrop-blur-lg
                    border-b border-gray-200"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-12 2xl:h-16 items-center">
            {/* Logo */}
            <div className="flex items-center">
              <a href="/" className="text-base md:text-lg lg:text-xl 2xl:text-2xl font-extrabold text-gray-700 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">
                TDAH Adulto Online
              </a>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-1">
              {menuItems.map((item) =>
                item.isServices ? (
                  <div key={item.id} className="relative" ref={servicesDropdownRef}>
                    <button
                      onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
                      className="relative px-3 py-1.5 2xl:px-4 2xl:py-2 text-sm 2xl:text-base font-medium text-gray-700 hover:text-gray-900 transition-all duration-300 group"
                    >
                      <span className="relative z-10">{item.label}</span>
                      {/* Efecto hover con fondo */}
                      <span className="absolute inset-0 rounded-lg bg-gradient-to-r from-cyan-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                      {/* Línea inferior animada */}
                      <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-cyan-600 group-hover:w-full group-hover:left-0 transition-all duration-300 ease-out"></span>
                    </button>
                    {/* Dropdown menu */}
                    {isServicesDropdownOpen && (
                      <div className="absolute left-0 mt-0 w-64 bg-white rounded-lg shadow-2xl z-[310] border border-gray-200 overflow-hidden">
                        {services.map((service, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleServiceClick(service)}
                            className="w-full text-left px-4 py-3 text-gray-700 hover:bg-cyan-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0 font-medium text-sm"
                          >
                            {service.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : item.isExternal ? (
                  <a
                    key={item.id}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative px-3 py-1.5 2xl:px-4 2xl:py-2 text-sm 2xl:text-base font-medium text-gray-700 hover:text-gray-900 transition-all duration-300 group"
                  >
                    <span className="relative z-10">{item.label}</span>
                    {/* Efecto hover con fondo */}
                    <span className="absolute inset-0 rounded-lg bg-gradient-to-r from-cyan-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    {/* Línea inferior animada */}
                    <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-cyan-600 group-hover:w-full group-hover:left-0 transition-all duration-300 ease-out"></span>
                  </a>
                ) : (
                  <a
                    key={item.id}
                    href={item.path}
                    className="relative px-3 py-1.5 2xl:px-4 2xl:py-2 text-sm 2xl:text-base font-medium text-gray-700 hover:text-gray-900 transition-all duration-300 group"
                  >
                    <span className="relative z-10">{item.label}</span>
                    {/* Efecto hover con fondo */}
                    <span className="absolute inset-0 rounded-lg bg-gradient-to-r from-cyan-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    {/* Línea inferior animada */}
                    <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-cyan-600 group-hover:w-full group-hover:left-0 transition-all duration-300 ease-out"></span>
                  </a>
                )
              )}
            </div>

            {/* Mute button (desktop: inline; mobile: next to hamburger) */}
            <div className="flex items-center gap-1">
              <button
                onClick={toggleMute}
                title={isMuted ? "Activar música" : "Silenciar música"}
                aria-label={isMuted ? "Activar música" : "Silenciar música"}
                className="p-2 rounded-lg text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 transition-all duration-200"
              >
                {isMuted ? (
                  /* Speaker off */
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707A1 1 0 0112 5v14a1 1 0 01-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                ) : (
                  /* Speaker on */
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M15.536 8.464a5 5 0 010 7.072M12 6v12m0 0l-4.243-4.243A1 1 0 007 13H4a1 1 0 01-1-1v-2a1 1 0 011-1h3a1 1 0 00.707-.293L12 6z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M18.364 5.636a9 9 0 010 12.728" />
                  </svg>
                )}
              </button>

              {/* Mobile hamburger */}
              <div className="md:hidden">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
                  className="p-2 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-blue-100 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu - Mejorado */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 inset-x-0 bg-white border-b border-gray-200 shadow-xl">
            <div className="px-4 py-3 space-y-1">
              {menuItems.map((item) =>
                item.isServices ? (
                  <div key={item.id} className="relative z-[310]">
                    <button
                      onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
                      className="w-full text-left block px-4 py-3 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-blue-100 rounded-lg transition-all duration-200"
                    >
                      {item.label}
                    </button>
                    {isServicesDropdownOpen && (
                      <div className="fixed left-4 right-4 mt-2 bg-white rounded-lg shadow-xl z-[310] border border-gray-200 py-2 pointer-events-auto" style={{ top: '70px' }}>
                        {services.map((service, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleServiceClick(service)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-cyan-50 transition-colors duration-200 pointer-events-auto"
                          >
                            {service.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : item.isExternal ? (
                  <a
                    key={item.id}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-blue-100 rounded-lg transition-all duration-200"
                  >
                    {item.label}
                  </a>
                ) : (
                  <a
                    key={item.id}
                    href={item.path}
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-blue-100 rounded-lg transition-all duration-200"
                  >
                    {item.label}
                  </a>
                )
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Contenido principal */}
      <main>
        {children}
      </main>

      {/* Resolución pantalla - solo en desarrollo */}
      {process.env.NODE_ENV === 'development' && <ResolutionBadge />}
    </div>
  );
};

export default MainLayout;